/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {AfterContentInit, Component, OnDestroy, OnInit } from '@angular/core';
import { DialogService } from "../../services/dialog.service";
import {Status} from "../../models/dialog-instance";
import { DialogInstance } from "../../models/dialog-instance";
import {DialogWebSocketService } from "../../services/dialog-web-socket.service";
import {DialogNotificationService} from "../../services/dialog-notification.service";
import {Subscription} from "rxjs";

declare var $: any;

export type SortDirection = 'ASC'|'DESC';

@Component({
  selector: 'app-settings',
  templateUrl: './security.component.html',
  styleUrls: ['./security.component.css']
})
export class SecurityComponent implements OnInit, AfterContentInit, OnDestroy {
  activeAlertSearchValue: string;
  closedAlertSearchValue: string;
  dialogInstancesOpen: DialogInstance[] = null;
  dialogInstancesClosed: DialogInstance[] = null;
  selectedDialogs: Map<string, boolean> = new Map<string, boolean>();

  activeAlertSortValue: string = "ID";
  activeAlertSortDirection: SortDirection = "DESC";
  closedAlertSortValue: string = "ID";
  closedAlertSortDirection: SortDirection = "DESC";

  // default page size and start page initialized here
  pageSize = 10;
  openStartPage = 0;
  closedStartPage = 0;

  // number of open and closed pages. Updated by getNumberOpenAlerts and getNumberClosedAlerts
  numberOpenPages = 0;
  numberClosedPages = 0;

  // number of open and closed alerts as an array. Allows ngFor to loop through pages
  numberOpenPagesAsArray = [];
  numberClosedPagesAsArray = [];

  refreshDialogsSubscription: Subscription;

  constructor(private dialogService: DialogService, private dialogWebSocketService: DialogWebSocketService, private dialogNotificationService: DialogNotificationService) {
  }

  ngOnInit(): void {
    this.refreshDialogs();

    this.refreshDialogsSubscription = this.dialogNotificationService.getRefreshDialogsListener().subscribe(result => {
      console.log("security component - dialogNotificationService called");
      this.refreshDialogs();
    });
  }

  ngOnDestroy(): void {
    if (!this.refreshDialogsSubscription) {
      this.refreshDialogsSubscription.unsubscribe();
    }
  }

  refreshDialogs(): void {
    // Get the top 10 open dialogs
    this.getOpenPagesOfPageSize(this.openStartPage);

    // Get the top 10 closed dialogs
    this.getClosedPagesOfPageSize(this.closedStartPage);

    // Get the number of active and closed alerts, which creates the paging
    this.getNumberOpenAlerts();
    this.getNumberClosedAlerts();
  }

  ngAfterContentInit(): void {
    //console.log("ngAfterContentInit() called");
  }

  dialogCheckboxSelected(dialogId: number, checked: boolean): void {
    // Set that this dialog is selected
    this.selectedDialogs[dialogId] = checked;
  }

  showAlertDialog(dialogInstance: DialogInstance): void {
    this.dialogWebSocketService.sendOnDemandDialogInstance(dialogInstance);
  }

  activeAlertSearchChanged(): void {
    // What do we search on? This has never been decided
  }

  closedAlertSearchChanged(): void {
    // What do we search on? This has never been decided
  }

  // Returns a number as an array so that looping can be done in an ngFor in Angular
  numberAsArray(value: number): number[] {
    return Array(value).fill(0).map((x, i) => i);
  }

  getNumberOpenPagesAsArray(numberOpenAlerts: number): number[] {
    this.numberOpenPages = Math.ceil(numberOpenAlerts / this.pageSize);
    return this.numberAsArray(this.numberOpenPages);
  }

  getNumberClosedPagesAsArray(numberClosedAlerts: number): number[] {
    this.numberClosedPages = Math.ceil(numberClosedAlerts / this.pageSize)
    return this.numberAsArray(this.numberClosedPages);
  }

  getOpenPagesOfPageSize(startPage: number): void {
    this.openStartPage = startPage;
    this.dialogService.getAllDialogInstancesOfStatus(Status.OPEN, startPage * this.pageSize, this.pageSize, this.activeAlertSortDirection, this.activeAlertSortValue).then(
      response => {
        this.dialogInstancesOpen = response;
        console.log(`Found ${this.dialogInstancesOpen.length} open dialogs`);

      }, error => {
        console.error("Could not get dialogInstances of Status " + Status.OPEN);
      }
    );
  }

  //KHT 08/05/2021 -  created getOpenPages() to be called when the table needs to be sorted. uses the openStartPage variable set in getOpenPagesOfPageSize, and
  //                  activeAlertSortDirection and activeAlertSortValue set by onSort()
  getOpenPages(): void {
    this.dialogService.getAllDialogInstancesOfStatus(Status.OPEN, this.openStartPage * this.pageSize, this.pageSize, this.activeAlertSortDirection, this.activeAlertSortValue).then(
      response => {
        this.dialogInstancesOpen = response;
        console.log(`Found ${this.dialogInstancesOpen.length} open dialogs`);

      }, error => {
        console.error("Could not get dialogInstances of Status " + Status.OPEN);
      }
    );
  }

  getClosedPagesOfPageSize(startPage: number): void {
    this.closedStartPage = startPage;
    this.dialogService.getAllDialogInstancesOfStatus(Status.CLOSED, startPage * this.pageSize, this.pageSize, this.closedAlertSortDirection, this.closedAlertSortValue).then(
      response => {
        this.dialogInstancesClosed = response;
        //console.log(`Found ${this.dialogInstancesClosed.length} closed dialogs`);

      }, error => {
        console.error("Could not get dialogInstances of Status " + Status.CLOSED);
      }
    );
  }

  //KHT 08/05/2021 -  created getClosedPages() to be called when the table needs to be sorted. uses the closedStartPage variable set in getClosedPagesOfPageSize, and
  //                  closedAlertSortDirection and closedAlertSortValue set by onSort()
  getClosedPages(): void {
    this.dialogService.getAllDialogInstancesOfStatus(Status.CLOSED, this.closedStartPage * this.pageSize, this.pageSize, this.closedAlertSortDirection, this.closedAlertSortValue).then(
      response => {
        this.dialogInstancesClosed = response;
        //console.log(`Found ${this.dialogInstancesClosed.length} closed dialogs`);

      }, error => {
        console.error("Could not get dialogInstances of Status " + Status.CLOSED);
      }
    );
  }

  getNumberOpenAlerts(): void {
    this.dialogService.getDialogInstanceStatusCount(Status.OPEN).then(
      response => {
        //console.log("number of open alerts is " + response);
        this.numberOpenPagesAsArray = this.getNumberOpenPagesAsArray(response.valueOf());
      },
      error => {
        console.error("Could not get the number of open alerts " + error);
      }
    )
  }

  getNumberClosedAlerts(): void {
    this.dialogService.getDialogInstanceStatusCount(Status.CLOSED).then(
      response => {
        //console.log("number of closed alerts is " + response);
        this.numberClosedPagesAsArray = this.getNumberClosedPagesAsArray(response.valueOf());
      },
      error => {
        console.error("Could not get the number of open alerts " + error);
      }
    )

  }

  moveStartOpenPage(): void {
    //console.log("move start open page");

    this.openStartPage = 0;
  }

  moveForwardOpenPage(): void {
    //console.log("move forward open page");
    if (this.openStartPage < (this.numberOpenPages - 5)) {
      this.openStartPage += 5;
    }
  }

  moveBackOpenPage(): void {
    //console.log("move back open page");

    if (this.openStartPage >= 5) {
      this.openStartPage -= 5;
    }
  }
  moveEndOpenPage(): void {
    //console.log("move end open page");

    this.openStartPage = Math.floor((this.numberOpenPages - 1) / 5) * 5;
  }

  moveStartClosePage(): void {
    this.closedStartPage = 0;
  }

  moveForwardClosePage(): void {
    if (this.closedStartPage < (this.numberClosedPages - 5)) {
      this.closedStartPage += 5;
    }
  }

  moveBackClosePage(): void {
    // Move back a page (
    if (this.closedStartPage >= 5) {
      this.closedStartPage -= 5;
    }
  }

  moveEndClosePage(): void {
    this.closedStartPage = Math.floor((this.numberClosedPages - 1) / 5) * 5;

  }


  //KHT 08/05/2021 - created onSort method to modify sorting values
  onSort(alertType: string, event: string) {
    //if the column header clicked is in the "ACTIVE ALERTS" table
    if(alertType == "ACTIVE"){
      //if this column is already being sorted on, switch sort direction
      if(this.activeAlertSortValue == event){
        if(this.activeAlertSortDirection == "ASC"){
          this.activeAlertSortDirection = "DESC";
        }else{
          this.activeAlertSortDirection = "ASC";
        }
        this.getOpenPages();
      }
      //if this column is not being sorted on yet, sort on it
      else{
        this.activeAlertSortValue = event;
        this.activeAlertSortDirection = "DESC";
        this.getOpenPages();
      }
    }
    //if the column header clicked is in the "CLOSED ALERTS" table 
    else if(alertType == "CLOSED"){ 
      console.log("CLOSED HIT")
      //if this column is already being sorted on, switch sort direction
      if(this.closedAlertSortValue == event){
        if(this.closedAlertSortDirection=="ASC"){
          this.closedAlertSortDirection = "DESC";
        }else{
          this.closedAlertSortDirection = "ASC";
        }
        this.getClosedPages();
      }
      //if this column is not being sorted on yet, sort on it
      else{
        this.closedAlertSortValue = event;
        this.closedAlertSortDirection = "DESC";
        this.getClosedPages();
      }
    }
  }
}
